/**
 * iter740 — Foodics-style split-payment dialog for the Cashier POS.
 *
 * Replaces the single-method "Confirm Payment" dialog with a flow
 * that lets the cashier split one bill across multiple methods:
 *   Cash 30 + Bilad-Bank 70 = paid 100 of 100.
 *
 * UX (matches the Foodics screenshot the user shared):
 *   1. Tap a payment-method chip (Cash / Bank / Card / Online / Credit)
 *   2. Amount-picker popup opens with presets `Full | 50 | 100 | Custom`
 *      (the "Full" chip pre-fills the *remaining* balance so a single
 *      tap closes the bill)
 *   3. Picked amount creates an "allocation row" — that row sums into
 *      the Paid total, the Remaining ticker counts down.
 *   4. PAY button activates only when Remaining ≤ 0.
 *   5. If Remaining < 0 (overpaid), the surplus is shown as Change Due.
 *
 * Data shape submitted on confirm:
 *   { payment_details: [
 *       { mode: 'cash',  amount: 30 },
 *       { mode: 'bank',  amount: 70, bank_account_id: 'abc-123' }
 *     ],
 *     notes: '...' }
 *
 * Backward compatible: a single-row split (e.g. 100% Cash) submits the
 * exact same payload shape as the legacy single-method flow, so the
 * Sales API does not need any changes.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Banknote, CreditCard, Smartphone, Users, Building2, X, Check, Plus, ArrowLeft, Printer, MessageCircle, Mail, Leaf, Loader2, Wifi, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import api from '@/lib/api';

const _authHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('cashier_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PAYMENT_METHODS = [
  { mode: 'cash',            label: 'Cash',           Icon: Banknote,    color: 'bg-success/10 text-success border-success/40' },
  { mode: 'bank',            label: 'Bank',           Icon: Building2,   color: 'bg-info/10 text-info border-info/40' },
  { mode: 'card',            label: 'Card',           Icon: CreditCard,  color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { mode: 'online_platform', label: 'Online',         Icon: Smartphone,  color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { mode: 'credit',          label: 'Credit (Customer)', Icon: Users,    color: 'bg-warning/10 text-warning border-warning/40' },
];

const BILL_OUTPUTS = [
  { key: 'print',    label: 'Print Bill',    Icon: Printer,        color: 'border-stone-300 text-stone-700',   active: 'bg-stone-800 text-white border-stone-800' },
  { key: 'whatsapp', label: 'WhatsApp',      Icon: MessageCircle,  color: 'border-emerald-300 text-emerald-700', active: 'bg-emerald-600 text-white border-emerald-600' },
  { key: 'email',    label: 'Email Bill',    Icon: Mail,           color: 'border-blue-300 text-blue-700',     active: 'bg-blue-600 text-white border-blue-600' },
  { key: 'go_green', label: 'Go Green',      Icon: Leaf,           color: 'border-green-300 text-green-700',    active: 'bg-green-600 text-white border-green-600' },
];

const PRESET_AMOUNTS = [50, 100, 200, 500];

export default function SplitPaymentDialog({
  open,
  onOpenChange,
  total,
  subtotal,
  discountAmount = 0,
  tax = 0,
  branchTax = { tax_name: 'VAT', tax_rate: 15 },
  currencySymbol = 'SAR',
  notes,
  setNotes,
  onConfirm,
  // iter746 — Bill output options. billOptions = enabled channels for
  // this branch; defaultContact prefills WhatsApp/Email from the
  // selected customer.
  billOptions = { print: true, whatsapp: true, email: true, go_green: true },
  defaultContact = { phone: '', email: '' },
  branchId = null,
  // iter845 — When the cashier taps a specific payment button (Cash /
  // Bank) from the cart, we open the dialog pre-focused on that
  // method's amount picker so they can type the tendered amount and
  // see change immediately, without one extra tap.
  initialMethod = null,
  initialBillMethod = 'print',
}) {
  const [allocations, setAllocations] = useState([]);
  const [activeMethod, setActiveMethod] = useState(null); // 'cash' | 'bank' | ...
  const [customAmount, setCustomAmount] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [pendingBankAccount, setPendingBankAccount] = useState('');
  const [bankLoaded, setBankLoaded] = useState(false);
  // Method tapped on the cart (Cash / Bank). Applied once the data it needs
  // (bank accounts) is loaded, so the dialog opens straight on Complete.
  const [quickMethod, setQuickMethod] = useState(null);

  // iter764 — Card payment-terminal integration.
  const [terminals, setTerminals] = useState([]);
  const [termStatus, setTermStatus] = useState('idle'); // idle|connecting|processing|approved|declined|error
  const [termResult, setTermResult] = useState(null);
  const [termError, setTermError] = useState('');

  // iter746 — Bill delivery selection.
  // iter893 — WhatsApp / Email were removed from the checkout picker so
  // the final payment window finishes immediately after the cashier
  // taps "Complete Order". Bills can still be sent to a customer's
  // WhatsApp or Email retroactively from Order History → Resend, which
  // prompts for the number/address only when it's actually needed.
  const enabledOutputs = useMemo(
    () => BILL_OUTPUTS.filter(o => o.key !== 'whatsapp' && o.key !== 'email' && billOptions?.[o.key] !== false),
    [billOptions],
  );
  const [billMethod, setBillMethod] = useState(initialBillMethod || 'print');
  // iter961 — Keep in sync with the cart-header "Go Green" toggle.
  useEffect(() => { setBillMethod(initialBillMethod || 'print'); }, [initialBillMethod]);
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // iter — Two-phase checkout. Once the bill is fully allocated the dialog
  // swaps the payment-method picker for a one-tap completion step whose
  // output (print vs paperless) is driven by the EXISTING cart "Go Green"
  // toggle. WhatsApp/Email stay optional under Go Green. greenChannel tracks
  // which optional digital-receipt input is expanded.
  const [greenChannel, setGreenChannel] = useState(null); // 'whatsapp' | 'email' | null

  // Reset on close so the next bill starts clean.
  useEffect(() => {
    if (!open) {
      setAllocations([]);
      setActiveMethod(null);
      setCustomAmount('');
      setPendingBankAccount('');
      setQuickMethod(null);
      setTermStatus('idle');
      setTermResult(null);
      setTermError('');
      setGreenChannel(null);
    } else {
      // Respect the cart-header "Go Green" toggle: when it's ON the caller
      // passes initialBillMethod='go_green', otherwise 'print'. (Previously
      // this was overridden to the first enabled output, which silently
      // ignored the toggle.)
      const def = enabledOutputs.find(o => o.key === 'print') || enabledOutputs[0];
      setBillMethod(initialBillMethod || (def ? def.key : 'print'));
      setContactPhone(defaultContact?.phone || '');
      setContactEmail(defaultContact?.email || '');
      // iter845 — auto-open the amount picker for the caller-chosen
      // method (Cash / Bank tapped from the cart) so the cashier lands
      // directly on the "amount received" input.
      if (initialMethod && ['cash', 'bank', 'card', 'online_platform'].includes(initialMethod)) {
        setQuickMethod(initialMethod);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // iter833 — Safety timeout: if a "connecting" or "processing" state
  // hangs longer than 45s (network wedged, terminal offline mid-call),
  // auto-flip to 'error' so the cashier can retry or pick another
  // method instead of staring at a frozen overlay.
  useEffect(() => {
    if (termStatus !== 'connecting' && termStatus !== 'processing') return;
    const t = setTimeout(() => {
      setTermStatus('error');
      setTermError('Terminal did not respond in time. Please retry or use another method.');
    }, 45000);
    return () => clearTimeout(t);
  }, [termStatus]);

  // Lazy-load bank accounts the first time the dialog opens.
  useEffect(() => {
    if (!open || bankAccounts.length > 0) return;
    (async () => {
      try {
        const res = await api.get('/bank-accounts');
        setBankAccounts(res.data?.data || res.data || []);
      } catch { /* ignore — cashier can still use Cash */ }
      setBankLoaded(true);
    })();
  }, [open, bankAccounts.length]);

  // Resolve the bank/card account with the SAME branch→bank rule accounting
  // already uses (backend bank_accounts._build_branch_bank_resolver, iter922):
  // an account is linked to every branch in `branch_ids` (legacy: `branch_id`),
  // and when a branch has several, the latest `active_from` <= today wins.
  // The previous version compared only `branch_id`, so a branch linked through
  // `branch_ids` (e.g. SNS → Alinma) matched nothing and the cashier had to
  // choose an account on every sale.
  const { bankPool, branchLinked } = useMemo(() => {
    const active = bankAccounts.filter(b => b.is_active !== false);
    const linkedTo = (b) => (b.branch_ids?.length ? b.branch_ids : (b.branch_id ? [b.branch_id] : []));
    const scoped = active.filter(b => linkedTo(b).includes(branchId));
    if (scoped.length) return { bankPool: scoped, branchLinked: true };
    const unlinked = active.filter(b => linkedTo(b).length === 0);
    return { bankPool: unlinked.length ? unlinked : active, branchLinked: false };
  }, [bankAccounts, branchId]);

  // The one account to auto-use. Null only when the branch has no linked
  // account AND the fallback list is ambiguous (several, none flagged
  // default) — the ONLY case the cashier is still asked to choose.
  const resolvedBankDefault = useMemo(() => {
    if (branchLinked) {
      const today = new Date().toISOString().slice(0, 10);
      const ranked = bankPool
        .map(b => [String(b.active_from || '0000-01-01').slice(0, 10), b])
        .sort((x, y) => x[0].localeCompare(y[0]));
      const current = ranked.filter(([af]) => af <= today);
      return (current.length ? current[current.length - 1] : ranked[0])[1];
    }
    return bankPool.find(b => b.is_default || b.default) || (bankPool.length === 1 ? bankPool[0] : null);
  }, [bankPool, branchLinked]);

  // Auto-pick whenever Bank becomes active and nothing is chosen yet (covers
  // the cart fast-path AND the late accounts-load race).
  useEffect(() => {
    if (activeMethod !== 'bank' || pendingBankAccount) return;
    if (resolvedBankDefault) setPendingBankAccount(resolvedBankDefault.id);
  }, [activeMethod, resolvedBankDefault, pendingBankAccount]);

  // True only when the cashier must still choose (ambiguous branch mapping).
  const needsBankChoice = activeMethod === 'bank' && !pendingBankAccount && bankPool.length > 0;

  // iter764 — Load configured payment terminals for this branch.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const qs = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : '';
        const res = await api.get(`/payment-terminals${qs}`, { headers: _authHeaders() });
        setTerminals((res.data?.terminals || []).filter(t => t.enabled !== false));
      } catch { setTerminals([]); }
    })();
  }, [open, branchId]);

  // The terminal a card payment will be sent to (default first, else first enabled).
  const cardTerminal = useMemo(
    () => terminals.find(t => t.is_default) || terminals[0] || null,
    [terminals],
  );

  // Normalize to currency (2dp) precision up-front. A VAT-inclusive total can
  // carry a binary floating-point tail (e.g. 20.000000000000004); comparing
  // the raw value left `paid >= total` (and the method-disabled check) subtly
  // false even when the bill was visually fully paid — PAY stayed disabled and
  // a second method could open on a 0 balance ("Online Full · SAR 0.00").
  const due = Math.round((Number(total) || 0) * 100) / 100;
  const paid = useMemo(
    () => Math.round(allocations.reduce((s, a) => s + (Number(a.amount) || 0), 0) * 100) / 100,
    [allocations],
  );
  const remaining = Math.max(0, Number((due - paid).toFixed(2)));
  const changeDue = paid > due ? Number((paid - due).toFixed(2)) : 0;
  const canPay = paid >= due && allocations.length > 0;

  // Add a payment row directly, without the amount screen.
  const addRow = (mode, amount, bankId = '') => {
    const row = { mode, amount: Number(Number(amount).toFixed(2)) };
    if (mode === 'bank' && bankId) {
      row.bank_account_id = bankId;
      row.bank_account_name = bankAccounts.find(b => b.id === bankId)?.bank_name || '';
    }
    setAllocations(prev => [...prev, row]);
  };

  // One tap = the full remaining balance on that method, so a normal sale is
  // Method → Complete. The amount screen only opens when it is genuinely
  // needed: Credit (customer flow), a bank with no resolvable account, or the
  // cashier tapping "Edit" on a row for a partial / split / tendered amount.
  const openAmountPicker = (mode) => {
    if (remaining > 0 && mode !== 'credit') {
      if (mode !== 'bank') { addRow(mode, remaining); return; }
      if (resolvedBankDefault) { addRow('bank', remaining, resolvedBankDefault.id); return; }
    }
    if (mode === 'bank' && !pendingBankAccount && resolvedBankDefault) {
      setPendingBankAccount(resolvedBankDefault.id);
    }
    setActiveMethod(mode);
    setCustomAmount('');
  };

  // Apply the cart-tapped method once bank accounts are known.
  useEffect(() => {
    if (!open || !quickMethod) return;
    if (quickMethod === 'bank' && !bankLoaded) return;
    setQuickMethod(null);
    openAmountPicker(quickMethod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quickMethod, bankLoaded]);

  // "Edit" on an allocation: take it back into the existing amount screen
  // (partial payment → split, or exact cash tendered for change).
  const editAllocation = (idx) => {
    const row = allocations[idx];
    setAllocations(prev => prev.filter((_, i) => i !== idx));
    setPendingBankAccount(row.bank_account_id || '');
    setCustomAmount('');
    setActiveMethod(row.mode);
  };

  // Cash tendered shortcuts on the Complete step (single cash row only).
  const cashIdx = allocations.findIndex(a => a.mode === 'cash');
  const singleCash = cashIdx >= 0 && allocations.filter(a => a.mode === 'cash').length === 1;
  const cashOwed = Math.max(0, Number((due - allocations.reduce((s, a) => s + (a.mode === 'cash' ? 0 : Number(a.amount) || 0), 0)).toFixed(2)));
  const setCashTendered = (amt) => setAllocations(prev => prev.map((a, i) => (i === cashIdx ? { ...a, amount: Number(Number(amt).toFixed(2)) } : a)));

  // iter — Fast default: when a single-method amount picker opens, pre-fill
  // the amount with the FULL remaining balance. The busy-hour cashier then
  // just taps "Add/Confirm" for a 100% payment (Cash shows it as "received",
  // editable upward for change). For a split, they simply type a smaller
  // amount, leaving a Remaining balance for the next method. Only fires on a
  // fresh method switch (customAmount empty) so it never overwrites typing.
  useEffect(() => {
    if (!activeMethod || activeMethod === 'credit') return;
    if (customAmount !== '' || remaining <= 0) return;
    setCustomAmount(String(remaining));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMethod]);

  // Change is only meaningful for physical cash. For bank/card/online/credit
  // an allocation must never exceed the remaining balance (no "change").
  const isCashMethod = activeMethod === 'cash';
  const exceedsRemaining = (amt) => !isCashMethod && Number(amt) > remaining + 0.005;

  const allocate = (amt) => {
    const n = Number(amt);
    if (!n || n <= 0) return;
    // Guard: non-cash methods cannot overpay the remaining balance.
    if (!isCashMethod && n > remaining + 0.005) return;
    const row = { mode: activeMethod, amount: Number(n.toFixed(2)) };
    if (activeMethod === 'bank' && pendingBankAccount) {
      row.bank_account_id = pendingBankAccount;
      row.bank_account_name = bankAccounts.find(b => b.id === pendingBankAccount)?.bank_name || '';
    }
    setAllocations(prev => [...prev, row]);
    setActiveMethod(null);
    setCustomAmount('');
    setPendingBankAccount('');
  };

  const removeAllocation = (idx) => {
    setAllocations(prev => prev.filter((_, i) => i !== idx));
  };

  const cardAmount = useMemo(
    () => allocations.filter(a => a.mode === 'card').reduce((s, a) => s + (Number(a.amount) || 0), 0),
    [allocations],
  );

  const _buildPayload = (terminalPayment = null, deliveryOverride = null) => ({
    payment_details: allocations.map(a => ({
      mode: a.mode,
      amount: a.amount,
      ...(a.bank_account_id ? { bank_account_id: a.bank_account_id } : {}),
    })),
    change_due: changeDue,
    bill_delivery: deliveryOverride || billMethod,
    contact: { phone: contactPhone.trim(), email: contactEmail.trim() },
    ...(terminalPayment ? { terminal_payment: terminalPayment } : {}),
  });

  const runTerminal = async (deliveryOverride = null) => {
    setTermError('');
    setTermStatus('connecting');
    try {
      await new Promise(r => setTimeout(r, 600));
      setTermStatus('processing');
      const { data } = await api.post(
        `/payment-terminals/${cardTerminal.id}/sale`,
        { amount: Number(cardAmount.toFixed(2)), branch_id: branchId },
        { headers: _authHeaders() },
      );
      setTermResult(data);
      if (data.status === 'approved') {
        setTermStatus('approved');
        const tp = {
          provider: data.provider, terminal_id: data.terminal_id,
          provider_tx_id: data.provider_tx_id, auth_code: data.raw_event?.auth_code,
          card_brand: data.raw_event?.card_brand, masked_pan: data.raw_event?.masked_pan,
          payment_id: data.id, mode: data.mode,
        };
        await new Promise(r => setTimeout(r, 700)); // let the cashier see "Approved"
        onConfirm(_buildPayload(tp, deliveryOverride));
      } else {
        setTermStatus('declined');
      }
    } catch (e) {
      setTermStatus('error');
      setTermError(e.response?.data?.detail || 'Could not reach the terminal');
    }
  };

  const handleConfirm = (deliveryOverride = null) => {
    if (!canPay) return;
    // Card allocation + a configured terminal → drive the terminal first.
    if (cardAmount > 0 && cardTerminal && termStatus === 'idle') {
      runTerminal(deliveryOverride);
      return;
    }
    onConfirm(_buildPayload(null, deliveryOverride));
  };

  // iter — Receipt-step completion. Sets the chosen bill output and finalizes
  // the sale in one tap (reuses handleConfirm → onConfirm → existing checkout).
  const complete = (delivery) => {
    setBillMethod(delivery);
    handleConfirm(delivery);
  };

  const fmt = (n) => Number(n || 0).toFixed(2);
  const methodLabel = (m) => PAYMENT_METHODS.find(p => p.mode === m)?.label || m;

  // iter — Receipt-output channels available for this branch (reuses the
  // existing billOptions flags). WhatsApp/Email appear as OPTIONAL digital
  // receipts under Go Green, reusing the same send endpoints as Order History.
  const whatsappEnabled = billOptions?.whatsapp !== false;
  const emailEnabled = billOptions?.email !== false;
  const printEnabled = billOptions?.print !== false;

  // iter — Contact validity for digital-receipt channels. Derived on every
  // render so switching channels always re-validates the current input and a
  // stale value can never leave "Send & Complete" incorrectly enabled.
  const phoneOk = (() => {
    const digits = (contactPhone || '').replace(/\D/g, '');
    return digits.length >= 7;
  })();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((contactEmail || '').trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[92dvh] overflow-y-auto overflow-x-hidden p-4 sm:p-5 gap-3" data-testid="split-payment-dialog">
        {/* iter764 — Card terminal status overlay.
            iter833 — Fixed the "black screen" bug: overlay now uses an
            explicit light/dark background instead of `bg-background/97`
            (which was rendering fully transparent-on-black under some
            themes). We also always render a "Cancel / Use another
            method" escape hatch, so a hung terminal can never trap the
            cashier. */}
        {termStatus !== 'idle' && (
          <div className="absolute inset-0 z-50 bg-white/97 dark:bg-stone-900/97 rounded-xl flex flex-col items-center justify-center p-8 text-center backdrop-blur-sm" data-testid="terminal-status-overlay">
            {(termStatus === 'connecting' || termStatus === 'processing') && (
              <>
                <Loader2 size={56} className="text-info animate-spin mb-4" />
                <h3 className="text-xl font-bold text-foreground" data-testid="terminal-status-text">
                  {termStatus === 'connecting' ? 'Connecting to terminal…' : 'Processing payment…'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {cardTerminal?.name} · {currencySymbol} {fmt(cardAmount)}
                </p>
                <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                  <Wifi size={12} /> Follow the prompt on the card machine
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-5"
                  onClick={() => { setTermStatus('idle'); setTermResult(null); setTermError(''); }}
                  data-testid="terminal-cancel-btn"
                >
                  <X size={14} className="mr-1" /> Cancel & use another method
                </Button>
              </>
            )}
            {termStatus === 'approved' && (
              <>
                <CheckCircle2 size={56} className="text-success mb-4" />
                <h3 className="text-xl font-bold text-success" data-testid="terminal-status-text">Approved</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {termResult?.raw_event?.card_brand} {termResult?.raw_event?.masked_pan} · Auth {termResult?.raw_event?.auth_code}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Ref: {termResult?.provider_tx_id}</p>
              </>
            )}
            {(termStatus === 'declined' || termStatus === 'error') && (
              <>
                <XCircle size={56} className="text-error mb-4" />
                <h3 className="text-xl font-bold text-error" data-testid="terminal-status-text">
                  {termStatus === 'declined' ? 'Payment declined' : 'Terminal error'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                  {termError || 'The card was declined. Ask the customer to retry or use another method.'}
                </p>
                <div className="flex gap-2 mt-5">
                  <Button onClick={() => runTerminal()} className="bg-orange-500 hover:bg-orange-600" data-testid="terminal-retry-btn">
                    <RefreshCw size={16} className="mr-1.5" /> Retry
                  </Button>
                  <Button variant="outline" onClick={() => { setTermStatus('idle'); setTermResult(null); setTermError(''); }} data-testid="terminal-other-method-btn">
                    Use another method
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Payment Methods</span>
            <span className="text-sm font-normal text-muted-foreground">
              Total <span className="font-bold text-foreground text-base ml-1">{currencySymbol} {fmt(total)}</span>
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Cart summary strip — iter831 compact one-row layout. */}
        <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs grid grid-cols-4 gap-2" data-testid="payment-summary-strip">
          <div><div className="text-[10px] text-muted-foreground">Subtotal</div><div className="font-semibold tabular-nums">{fmt(subtotal)}</div></div>
          <div><div className="text-[10px] text-muted-foreground">Discount</div><div className="font-semibold tabular-nums text-error">{discountAmount > 0 ? `-${fmt(discountAmount)}` : '—'}</div></div>
          <div><div className="text-[10px] text-muted-foreground">{branchTax?.tax_name || 'Tax'} ({branchTax?.tax_rate || 0}%)</div><div className="font-semibold tabular-nums">{fmt(tax)}</div></div>
          <div><div className="text-[10px] text-muted-foreground">Total</div><div className="font-bold tabular-nums text-orange-600">{fmt(total)}</div></div>
        </div>

        {/* PHASE 1 — "Add payment": method picker / amount picker. Hidden
            once the bill is fully covered (canPay) so the cashier isn't shown
            payment methods they no longer need. While a balance remains it
            stays visible, which is exactly the split / partial-payment path. */}
        {!canPay && (!activeMethod ? (
          <div className="grid grid-cols-2 gap-2" data-testid="payment-method-list">
            {PAYMENT_METHODS.map(({ mode, label, Icon, color }) => (
              <button
                key={mode}
                type="button"
                onClick={() => openAmountPicker(mode)}
                disabled={remaining === 0 && changeDue === 0 && paid >= due}
                className={`flex items-center gap-2 px-3 py-3 rounded-lg border-2 transition-all active:scale-[0.98] ${color} disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid={`payment-method-${mode}`}
              >
                <Icon size={18} />
                <span className="font-semibold text-sm">{label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="amount-picker">
            <button
              type="button"
              onClick={() => { setActiveMethod(null); setCustomAmount(''); setPendingBankAccount(''); }}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid="amount-picker-back"
            >
              <ArrowLeft size={14} /> Back to methods
            </button>
            <div className="text-sm font-semibold capitalize">
              {activeMethod === 'cash'
                ? 'Cash received from customer'
                : `${methodLabel(activeMethod)} — pick amount`}
            </div>
            {activeMethod === 'cash' && (
              <div className="text-[11px] text-muted-foreground -mt-1">
                Enter the exact cash the customer handed over. Change due will be calculated automatically.
              </div>
            )}

            {activeMethod === 'bank' && bankPool.length > 1 && !resolvedBankDefault && (
              <div className="space-y-1">
                <Label className="text-xs">Bank account</Label>
                <Select value={pendingBankAccount} onValueChange={setPendingBankAccount}>
                  <SelectTrigger data-testid="bank-account-select">
                    <SelectValue placeholder="Choose bank account…" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankPool.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.bank_name}{b.account_number ? ` · ${b.account_number.slice(-4)}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-12 text-base font-semibold border-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                onClick={() => allocate(remaining)}
                disabled={remaining === 0 || needsBankChoice}
                data-testid="amount-preset-full"
              >
                Full · {currencySymbol} {fmt(remaining)}
              </Button>
              {PRESET_AMOUNTS.map(amt => (
                <Button
                  key={amt}
                  type="button"
                  variant="outline"
                  className="h-12 text-base font-semibold"
                  onClick={() => allocate(amt)}
                  disabled={needsBankChoice || exceedsRemaining(amt)}
                  data-testid={`amount-preset-${amt}`}
                >
                  {currencySymbol} {fmt(amt)}
                </Button>
              ))}
            </div>

            <div className="flex items-end gap-2 pt-1">
              <div className="flex-1">
                <Label className="text-xs">
                  {activeMethod === 'cash' ? 'Cash received' : 'Custom amount'}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  onKeyDown={(e) => {
                    // iter845 — Enter key confirms allocation, matching
                    // the physical-keypad muscle memory cashiers use on
                    // legacy POS terminals.
                    if (e.key === 'Enter' && customAmount && Number(customAmount) > 0) {
                      const canAllocateBank = !needsBankChoice;
                      if (canAllocateBank) {
                        e.preventDefault();
                        allocate(customAmount);
                      }
                    }
                  }}
                  placeholder="0.00"
                  className="text-base h-11"
                  data-testid="amount-custom-input"
                />
              </div>
              <Button
                type="button"
                onClick={() => allocate(customAmount)}
                disabled={!customAmount || Number(customAmount) <= 0 || needsBankChoice || exceedsRemaining(customAmount)}
                className="h-11 px-5 bg-orange-500 hover:bg-orange-600"
                data-testid="amount-custom-add"
              >
                <Plus size={16} className="mr-1" /> Add
              </Button>
            </div>

            {/* iter833 — Live change preview for cash payments so the
                cashier sees the change before committing the allocation.
                iter845 — Enlarged for readability from behind the counter. */}
            {activeMethod === 'cash' && Number(customAmount) > 0 && (
              <div
                className={`rounded-lg border-2 px-4 py-3 flex items-center justify-between ${
                  Number(customAmount) >= remaining
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-amber-300 bg-amber-50 text-amber-800'
                }`}
                data-testid="cash-change-preview"
              >
                <span className="font-semibold text-sm uppercase tracking-wide">
                  {Number(customAmount) >= remaining ? 'Change to return' : 'Still owed'}
                </span>
                <span className="font-black tabular-nums text-lg sm:text-xl" data-testid="cash-change-amount">
                  {currencySymbol} {fmt(Math.abs(Number(customAmount) - remaining))}
                </span>
              </div>
            )}
          </div>
        ))}

        {/* Allocations list (the "ledger" of what's been added so far) */}
        {allocations.length > 0 && (
          <div className="rounded-lg border border-border bg-card" data-testid="allocations-list">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Allocations</div>
            <div className="divide-y">
              {allocations.map((a, idx) => {
                const M = PAYMENT_METHODS.find(p => p.mode === a.mode);
                const Icon = M?.Icon || Banknote;
                return (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2" data-testid={`allocation-row-${idx}`}>
                    <Icon size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium">{M?.label || a.mode}</span>
                    {a.bank_account_name && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{a.bank_account_name}</span>
                    )}
                    <span className="ml-auto tabular-nums font-semibold">{currencySymbol} {fmt(a.amount)}</span>
                    <button
                      type="button"
                      onClick={() => editAllocation(idx)}
                      className="text-xs text-info hover:underline"
                      data-testid={`allocation-edit-${idx}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => removeAllocation(idx)}
                      className="text-muted-foreground hover:text-error transition-colors"
                      data-testid={`allocation-remove-${idx}`}
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Running ticker — Foodics-style "Remaining" at the bottom */}
        <div className="rounded-xl bg-gradient-to-br from-stone-50 to-stone-100 border-2 p-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid</div>
            <div className="text-lg font-bold text-success tabular-nums" data-testid="paid-total">{currencySymbol} {fmt(paid)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Remaining</div>
            <div className={`text-lg font-bold tabular-nums ${remaining === 0 ? 'text-success' : 'text-warning'}`} data-testid="remaining-total">{currencySymbol} {fmt(remaining)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Change due</div>
            <div className={`text-lg font-bold tabular-nums ${changeDue > 0 ? 'text-orange-600' : 'text-muted-foreground'}`} data-testid="change-due">{currencySymbol} {fmt(changeDue)}</div>
          </div>
        </div>

        {/* Notes — only while still building the payment (phase 1). */}
        {!canPay && (
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions…"
              rows={2}
              className="mt-1"
              data-testid="payment-notes"
            />
          </div>
        )}

        {/* PHASE 2 — one-tap completion. Appears automatically once the bill
            is fully paid. Receipt OUTPUT is driven by the EXISTING cart
            "Go Green" toggle (billMethod), not a duplicate chooser:
              • toggle OFF  → "Complete & Print"  → auto-prints the receipt
              • toggle ON   → "Complete (No Paper)" + OPTIONAL WhatsApp/Email
            WhatsApp/Email are never mandatory — the cashier can finish
            instantly. Reuses bill_delivery + the existing send endpoints. */}
        {canPay ? (
          <div className="space-y-3" data-testid="receipt-step">
            <div className="text-sm font-semibold text-success flex items-center gap-1.5">
              <CheckCircle2 size={16} />
              {changeDue > 0
                ? `Fully paid — change ${currencySymbol} ${fmt(changeDue)}`
                : 'Bill fully paid'}
            </div>

            {singleCash && cashOwed > 0 && (
              <div data-testid="cash-tendered">
                <Label className="text-xs">Cash received</Label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-1">
                  {[cashOwed, ...PRESET_AMOUNTS.filter(amt => amt > cashOwed)].map(amt => (
                    <Button
                      key={amt}
                      type="button"
                      variant="outline"
                      onClick={() => setCashTendered(amt)}
                      className={`h-11 font-semibold ${Math.abs((allocations[cashIdx]?.amount || 0) - amt) < 0.005 ? 'border-2 border-orange-500 text-orange-700 bg-orange-50' : ''}`}
                      data-testid={`cash-tendered-${amt === cashOwed ? 'exact' : amt}`}
                    >
                      {amt === cashOwed ? `Exact ${fmt(amt)}` : fmt(amt)}
                    </Button>
                  ))}
                  <Button type="button" variant="outline" onClick={() => editAllocation(cashIdx)} className="h-11 font-semibold" data-testid="cash-tendered-other">
                    Other
                  </Button>
                </div>
              </div>
            )}

            {/* iter — Receipt delivery is available after EVERY completed
                payment method (Cash / Bank / Card / Online / Credit / Split),
                not just Go Green. Channels are gated only by the branch Bill
                Delivery settings (billOptions). Go Green ON suppresses physical
                print; contact details are required only when WhatsApp/Email is
                the chosen channel. */}
            {billMethod === 'go_green' ? (
              <div className="space-y-2" data-testid="go-green-complete">
                <Button
                  onClick={() => complete('go_green')}
                  className="w-full h-14 bg-green-600 hover:bg-green-700 text-base font-bold"
                  data-testid="complete-go-green"
                >
                  <Leaf size={18} className="mr-2" />
                  {cardAmount > 0 && cardTerminal ? 'Charge Card — No Paper' : 'Complete — No Paper'}
                </Button>
                {(whatsappEnabled || emailEnabled) && (
                  <>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="flex-1 h-px bg-border" />
                      Optional digital receipt
                      <span className="flex-1 h-px bg-border" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {whatsappEnabled && (
                        <Button
                          variant="outline"
                          onClick={() => setGreenChannel(greenChannel === 'whatsapp' ? null : 'whatsapp')}
                          className={`h-11 border-2 ${greenChannel === 'whatsapp' ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-600' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          data-testid="green-whatsapp"
                        >
                          <MessageCircle size={16} className="mr-1.5" /> WhatsApp
                        </Button>
                      )}
                      {emailEnabled && (
                        <Button
                          variant="outline"
                          onClick={() => setGreenChannel(greenChannel === 'email' ? null : 'email')}
                          className={`h-11 border-2 ${greenChannel === 'email' ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-600' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                          data-testid="green-email"
                        >
                          <Mail size={16} className="mr-1.5" /> Email
                        </Button>
                      )}
                    </div>
                  </>
                )}
                {greenChannel === 'whatsapp' && whatsappEnabled && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Customer WhatsApp number</Label>
                      <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="05XXXXXXXX" className="mt-1 h-11" data-testid="green-whatsapp-input" />
                    </div>
                    <Button onClick={() => complete('whatsapp')} disabled={!phoneOk} className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700" data-testid="green-whatsapp-send">Send &amp; Complete</Button>
                  </div>
                )}
                {greenChannel === 'email' && emailEnabled && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Customer email</Label>
                      <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="name@example.com" className="mt-1 h-11" data-testid="green-email-input" />
                    </div>
                    <Button onClick={() => complete('email')} disabled={!emailOk} className="h-11 px-4 bg-blue-600 hover:bg-blue-700" data-testid="green-email-send">Send &amp; Complete</Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2" data-testid="receipt-delivery">
                {printEnabled && (
                  <Button
                    onClick={() => complete('print')}
                    className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-base font-bold"
                    data-testid="complete-print"
                  >
                    <Printer size={18} className="mr-2" />
                    {cardAmount > 0 && cardTerminal ? 'Charge Card & Print' : 'Complete & Print'}
                  </Button>
                )}
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="flex-1 h-px bg-border" />
                  {printEnabled ? 'Or send digitally / no paper' : 'Send digitally / no paper'}
                  <span className="flex-1 h-px bg-border" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {whatsappEnabled && (
                    <Button
                      variant="outline"
                      onClick={() => setGreenChannel(greenChannel === 'whatsapp' ? null : 'whatsapp')}
                      className={`h-11 border-2 ${greenChannel === 'whatsapp' ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-600' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                      data-testid="delivery-whatsapp"
                    >
                      <MessageCircle size={16} className="mr-1.5" /> WhatsApp
                    </Button>
                  )}
                  {emailEnabled && (
                    <Button
                      variant="outline"
                      onClick={() => setGreenChannel(greenChannel === 'email' ? null : 'email')}
                      className={`h-11 border-2 ${greenChannel === 'email' ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-600' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                      data-testid="delivery-email"
                    >
                      <Mail size={16} className="mr-1.5" /> Email
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => complete('go_green')}
                    className="h-11 border-2 border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="delivery-no-paper"
                  >
                    <Leaf size={16} className="mr-1.5" /> No Paper
                  </Button>
                </div>
                {greenChannel === 'whatsapp' && whatsappEnabled && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Customer WhatsApp number</Label>
                      <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="05XXXXXXXX" className="mt-1 h-11" data-testid="delivery-whatsapp-input" />
                    </div>
                    <Button onClick={() => complete('whatsapp')} disabled={!phoneOk} className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700" data-testid="delivery-whatsapp-send">Send &amp; Complete</Button>
                  </div>
                )}
                {greenChannel === 'email' && emailEnabled && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Customer email</Label>
                      <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="name@example.com" className="mt-1 h-11" data-testid="delivery-email-input" />
                    </div>
                    <Button onClick={() => complete('email')} disabled={!emailOk} className="h-11 px-4 bg-blue-600 hover:bg-blue-700" data-testid="delivery-email-send">Send &amp; Complete</Button>
                  </div>
                )}
              </div>
            )}

            <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full text-muted-foreground" data-testid="payment-cancel">Cancel</Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="payment-cancel">Cancel</Button>
            <div
              className="flex-[2] h-12 rounded-md border-2 border-dashed border-muted-foreground/30 flex items-center justify-center text-sm text-muted-foreground px-2 text-center"
              data-testid="remaining-hint"
            >
              {paid > 0 ? `Add ${currencySymbol} ${fmt(remaining)} to finish` : 'Select a payment method'}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
